import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Save, Upload } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleER } from "@/components/er/ModuleTitleER";

export default function ErResourceAdmin() {
  const [resources, setResources] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/er/resources", {
        params: { include_archived: showArchived },
      });
      setResources(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
     
  }, [showArchived]);

  const upload = async () => {
    if (!file || !title || !category) {
      toast.error("Please add a title, category, and file.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", title);
      form.append("category", category);
      if (description) form.append("description", description);
      await api.post("/er/resources", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTitle("");
      setCategory("");
      setDescription("");
      setFile(null);
      const el = document.getElementById("er-resource-file-input");
      if (el) el.value = "";
      toast.success("Resource uploaded.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (r) => {
    const e = edits[r.id] || {};
    setBusy(true);
    try {
      await api.put(`/er/resources/${r.id}`, {
        title: e.title ?? r.title,
        category: e.category ?? r.category,
        description: e.description ?? r.description ?? "",
      });
      toast.success("Saved.");
      setEdits({ ...edits, [r.id]: undefined });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (r) => {
    setBusy(true);
    try {
      await api.post(`/er/resources/${r.id}/archive`);
      toast.success("Archived.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="er-resource-admin-page">
      <div>
        <ModuleTitleER />
        <h1 className="mt-3 font-serif text-forest-900 text-4xl">Resource Library</h1>
        <p className="mt-2 text-charcoal-soft max-w-2xl">
          Policies, handbooks, and forms staff can search and download from the ER Center landing
          page.
        </p>
      </div>

      <section className="patria-card p-6" data-testid="er-resource-upload-form">
        <h3 className="font-serif text-forest-900 text-xl">Upload A New Document</h3>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="patria-label" htmlFor="rt">Title *</label>
            <input
              id="rt"
              className="patria-input mt-2"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Employee Handbook v2.1"
              data-testid="er-resource-title"
            />
          </div>
          <div>
            <label className="patria-label" htmlFor="rc">Category *</label>
            <input
              id="rc"
              className="patria-input mt-2"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Handbook / Policy / Form / Agreement"
              data-testid="er-resource-category"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="patria-label" htmlFor="rd">Description (optional)</label>
          <textarea
            id="rd"
            className="patria-input mt-2"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="er-resource-description"
          />
        </div>
        <div className="mt-4">
          <label className="patria-label" htmlFor="er-resource-file-input">File *</label>
          <input
            id="er-resource-file-input"
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-2 block text-sm"
            data-testid="er-resource-file"
          />
          <p className="text-xs text-charcoal-muted mt-1">Max 10 MB. PDF, DOC, image, or TXT.</p>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            onClick={upload}
            disabled={busy}
            className="patria-btn-primary"
            data-testid="er-resource-upload"
          >
            <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </section>

      <div className="flex justify-end">
        <label className="flex items-center gap-2 text-sm text-charcoal-soft">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="er-resource-show-archived"
          />
          Show archived
        </label>
      </div>

      <section className="patria-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-forest-600 text-cream-100">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Title</th>
              <th className="text-left px-4 py-3 font-medium">Category</th>
              <th className="text-left px-4 py-3 font-medium">Description</th>
              <th className="text-left px-4 py-3 font-medium">State</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-sage-300/60">
            {resources.map((r) => {
              const draft = edits[r.id] || {};
              return (
                <tr key={r.id} data-testid={`er-resource-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <input
                      className="patria-input py-1.5 text-sm"
                      value={draft.title ?? r.title}
                      onChange={(e) => setEdits({ ...edits, [r.id]: { ...draft, title: e.target.value } })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      className="patria-input py-1.5 text-sm"
                      value={draft.category ?? r.category}
                      onChange={(e) => setEdits({ ...edits, [r.id]: { ...draft, category: e.target.value } })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <textarea
                      className="patria-input py-1.5 text-sm"
                      rows={2}
                      value={draft.description ?? r.description ?? ""}
                      onChange={(e) => setEdits({ ...edits, [r.id]: { ...draft, description: e.target.value } })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {r.archived ? (
                      <span className="inline-flex items-center rounded-full bg-charcoal/20 text-charcoal px-2 py-0.5 text-xs">Archived</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-forest-600 text-cream-100 px-2 py-0.5 text-xs">Live</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-2 items-end">
                      <button
                        onClick={() => saveEdit(r)}
                        className="patria-btn-secondary text-xs py-1.5 px-3"
                        data-testid={`er-resource-save-${r.id}`}
                      >
                        <Save className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                        Save
                      </button>
                      {!r.archived && (
                        <button
                          onClick={() => archive(r)}
                          className="patria-btn-secondary text-xs py-1.5 px-3"
                          data-testid={`er-resource-archive-${r.id}`}
                        >
                          <Archive className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                          Archive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {resources.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-charcoal-muted italic">
                  No resources.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
